import { useEffect, useState } from 'react';

import { DateRange } from 'react-day-picker';

import { Chip, type ChipStatus } from '@appcharge/shared-ui';
import { CellContext, type ColumnDef, type Row } from '@tanstack/react-table';
import {
  type FormikProps,
  ScheduleTimeFrame,
  type TimeFrameObject
} from 'common/contracts';
import { DATE_TIME_FORMAT_SHORTER } from 'constants/constants';
import {
  EAppearanceStatus,
  EPendingAppearanceStatus,
  OfferType
} from 'constants/enums';
import dayjs from 'dayjs';
import { getDurationTextFromMinutes } from 'utils/formattingUtil';
import { statusUtils } from 'utils/scheduleUtils';

import AcInput from 'components/AcInput/AcInput';
import Button from 'components/Button/Button';
import ActionCell from 'components/DataTableComponent/components/ActionCell';
import DataTable from 'components/DataTableComponent/DataTable';
import DatePicker from 'components/DatePicker/DatePicker';
import { DatePickerMode } from 'components/DatePicker/DatePicker.types';
import Input from 'components/Input/Input';
import { Flex } from 'components/ui/flex';

interface ScheduledOfferProps {
  formikProps: FormikProps;
  intervalCron: string;
  appearancesTableData: ScheduleTimeFrame[];
  resetScheduleValues: () => void;
}

const statusMapping: Record<string, ChipStatus> = {
  [EAppearanceStatus.RUNNING]: 'success',
  [EAppearanceStatus.UPCOMING]: 'warning',
  [EAppearanceStatus.ENDED]: 'secondary',
  [EPendingAppearanceStatus.PENDING_SAVE]: 'secondary'
};

const ScheduledOffer: React.FC<ScheduledOfferProps> = ({
  formikProps,
  intervalCron,
  appearancesTableData,
  resetScheduleValues
}) => {
  const { values, handleChange, handleBlur, setFieldValue, touched, errors } =
    formikProps;
  const [dateRange, setDateRange] = useState<DateRange>({
    from: undefined,
    to: undefined
  });
  const [editedTimeFrame, setEditedTimeFrame] =
    useState<ScheduleTimeFrame | null>(null);
  const [formattedDuration, setFormattedDuration] = useState<string | null>(
    null
  );
  const [notes, setNotes] = useState('');

  const resetForm = () => {
    setEditedTimeFrame(null);
    setFormattedDuration(null);
    setNotes('');
    setDateRange({ from: undefined, to: undefined });
  };

  const handleApply = (range: DateRange, notes?: string) => {
    const fromUtcDate = range.from;
    const toUtcDate = range.to;

    if (fromUtcDate && toUtcDate) {
      const newTimeFrame: ScheduleTimeFrame = {
        startTime: new Date(fromUtcDate.toISOString()),
        endTime: new Date(toUtcDate.toISOString()),
        notes
      };
      addTimeFrameToSchedule(newTimeFrame);
      resetForm();
    } else {
      console.error('Invalid date range provided: Start or End date is null.');
    }
  };

  const removeTimeframe = (startTime: string, endTime: string) => {
    const newTimeFrames = formikProps?.values?.schedule?.timeFrames?.filter(
      (timeFrame: TimeFrameObject) =>
        new Date(timeFrame.startTime).getTime() !==
        new Date(startTime).getTime() &&
        new Date(timeFrame.endTime).getTime() !== new Date(endTime).getTime()
    );

    resetScheduleValues();
    formikProps.setFieldValue('schedule', {
      permanent: !newTimeFrames.length
        ? false
        : formikProps.values?.schedule.permanent,
      timeFrames: newTimeFrames
    });
  };

  const handleClear = () => {
    addTimeFrameToSchedule(editedTimeFrame!);
    resetForm();
    resetScheduleValues();
  };

  const addTimeFrameToSchedule = (newTimeFrame: ScheduleTimeFrame) => {
    const newTimeFramesArray = [
      ...values?.schedule?.timeFrames,
      newTimeFrame
    ].sort(
      (a: ScheduleTimeFrame, b: ScheduleTimeFrame) =>
        new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
    );
    setFieldValue('schedule', {
      permanent: values?.schedule.permanent,
      timeFrames: newTimeFramesArray,
      ...(intervalCron &&
        values?.schedule?.permanent && { interval: intervalCron })
    });
  };

  useEffect(() => {
    if (dateRange.from && dateRange.to) {
      const diffMinutes = dayjs(dateRange.to).diff(
        dayjs(dateRange.from),
        'minute'
      );
      const duration = getDurationTextFromMinutes(diffMinutes);
      setFormattedDuration(duration);
    }
  }, [dateRange]);

  const columns: ColumnDef<ScheduleTimeFrame>[] = [
    {
      accessorKey: 'startTime',
      header: 'Start',
      cell: (cell: CellContext<ScheduleTimeFrame, unknown>) =>
        dayjs(cell.getValue() as string)
          .utc()
          .format(DATE_TIME_FORMAT_SHORTER)
    },
    {
      accessorKey: 'endTime',
      header: 'End',
      cell: (cell: CellContext<ScheduleTimeFrame, unknown>) =>
        dayjs(cell.getValue() as string)
          .utc()
          .format(DATE_TIME_FORMAT_SHORTER)
    },
    {
      accessorKey: 'notes',
      header: 'Notes',
      cell: (cell: CellContext<ScheduleTimeFrame, unknown>) =>
        cell.getValue() as string
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }: { row: Row<ScheduleTimeFrame> }) => {
        const { startTime, endTime, id } = row.original;
        const pendingSaveStatus = id?.startsWith('id-');
        const status = pendingSaveStatus
          ? EPendingAppearanceStatus.PENDING_SAVE
          : statusUtils.determineStatus(new Date(startTime), new Date(endTime));

        return (
          <Chip variant="flat" status={statusMapping[status]}>
            {status}
          </Chip>
        );
      }
    },
    {
      accessorKey: 'actions',
      header: '',
      cell: ({ row }: { row: Row<ScheduleTimeFrame> }) => (
        <ActionCell
          rowData={row.original}
          actions={[
            {
              label: 'Edit',
              handler: () => {
                setEditedTimeFrame(row.original);
                setDateRange({
                  from: new Date(row.original.startTime),
                  to: new Date(row.original.endTime)
                });
                setNotes(row.original.notes || '');
                removeTimeframe(
                  String(row.original.startTime),
                  String(row.original.endTime)
                );
              }
            },
            {
              label: 'Delete',
              handler: () =>
                removeTimeframe(
                  String(row.original.startTime),
                  String(row.original.endTime)
                )
            }
          ]}
        />
      )
    }
  ];

  const tableConfig = {
    data: appearancesTableData,
    showPagination: false,
    columns
  };

  return (
    <div className="mt-8">
      {values.type !== OfferType.ROLLING_OFFER && (
        <>
          <Input
            label="Availability"
            name="playerAvailability"
            value={values.playerAvailability || ''}
            placeholder="Set availability"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleChange(e)
            }
            onBlur={handleBlur}
            isRequired={values.setAsFree}
            type="number"
            className="w-64"
            state={
              touched.playerAvailability && errors.playerAvailability
                ? 'error'
                : 'default'
            }
            description={
              touched.playerAvailability && errors.playerAvailability
                ? errors.playerAvailability
                : ''
            }
            onKeyDown={(e) => {
              if (e.key === '-' || e.key === 'Minus') {
                e.preventDefault();
              }
            }}
            min={0}
          />
          <div className="my-5 border-t py-1" />
        </>
      )}
      <Flex direction="row" justify="start" className="gap-2 pt-2">
        <DatePicker
          shouldDisplayTime={true}
          shouldExcludePast={true}
          mode={DatePickerMode.RANGE}
          value={dateRange}
          onApply={(value: any) => {
            setDateRange(value);
          }}
        />
      </Flex>
      {formattedDuration && (
        <Flex
          direction="row"
          justify="start"
          className="gap-2 pt-2 mt-2"
        >
          <AcInput
            value={formattedDuration}
            header={'Duration'}
            disabled
            sx={{
              width: `${formattedDuration?.length || 0}ch`,
              minWidth: 'fit-content'
            }}
          />
        </Flex>
      )}
      <Flex direction="row" justify="start" className="gap-2 pt-2 mt-2">
        <Input
          label="Notes"
          name="notes"
          value={notes}
          placeholder="Type your note here"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setNotes(e.target.value)
          }
        />
      </Flex>
      {editedTimeFrame !== null && (
        <Flex
          direction="row"
          justify="start"
          className="gap-2 pt-2 mt-2"
        >
          <Button
            onClick={() => {
              handleClear();
            }}
            className="w-full bg-base-accent text-base-accentForeground hover:bg-button-hover-create"
          >
            Cancel
          </Button>
        </Flex>
      )}
      <Flex direction="row" justify="start" className="gap-2 pt-2 mt-2">
        <Button
          disabled={
            !dateRange.from ||
            !dateRange.to ||
            (formikProps?.touched.playerAvailability &&
              Boolean(formikProps?.errors.playerAvailability))
          }
          onClick={() => {
            handleApply(dateRange, notes);
            resetScheduleValues();
          }}
          className="w-full bg-base-accent text-base-accentForeground"
          isCreateButton
        >
          {editedTimeFrame !== null ? 'Apply' : 'Add Appearance'}
        </Button>
      </Flex>
      {appearancesTableData.length > 0 && (
        <div className="rounded-[20px] mt-8 mb-10 w-[600px] relative left-1/2 transform -translate-x-1/2">
          <DataTable {...tableConfig} />
        </div>
      )}
    </div>
  );
};

export default ScheduledOffer;
